import { useContext, useEffect, useState } from 'react';
import { graphqlFetch } from '@shared/api';
import { sectionQueryDocument } from './sections.document';
import { useQuery } from '@tanstack/react-query';

import { Issue, Section, USER_ROLE } from '../../../../constants';
import config from '@/config';
import { byReleaseDate } from '@shared/lib/hooks';
import { AppContext } from '../../../../app.context';
import { getQueryCacheOptions } from '@shared/query-client/query-client';

export function useSection(id: number): GetIssuesResponse {
	const {
		parseGraphQLClientError,
		onlineStatus: isOnline,
	} = useContext(AppContext);
	const [issues, setIssues] = useState<Array<Issue>>([]);

	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	const cacheOptions = getQueryCacheOptions({
		isOnline,
		isAdmin,
		ttlMinutes: config.app?.cache?.ttl?.getAllIssues,
	});
	// variables: { id: sectionId },
	const queryKey = ['sections', id, 'issues'];
	const { isPending, error, data } = useQuery<SectionQuery>({
		queryKey,
		queryFn: async () =>
			// Cast: generated type's nullability is honest where the shared
			// Section/Issue types claim required fields; reconciling that is
			// topic 5's separate "derive Issue/Article/Style from generated
			// types" step.
			graphqlFetch(sectionQueryDocument, {
				id,
			}) as unknown as Promise<SectionQuery>,
		staleTime: cacheOptions.staleTime,
		refetchInterval: cacheOptions.refetchInterval,
		gcTime: cacheOptions.gcTime,
	});

	useEffect(() => {
		if (!data) return;
		const { section } = data;
		if (!section) return;
		if (!section.issues) return;

		setIssues(section.issues.sort(byReleaseDate));
	}, [data]);

	return {
		isLoading: isPending,
		error: error ? parseGraphQLClientError(error) : null,
		issues,
	};
}

interface SectionQuery {
	section: Section | null;
}

interface GetIssuesResponse {
	isLoading: boolean;
	error: Error | null;
	issues: Array<Issue>;
}
